import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import { getPushState, isError, sendTestMessage } from './api';
import {
  disablePush,
  enablePush,
  isApple,
  installed,
  permission,
  subscribedHere,
  support,
} from './push';

/**
 * Notifications, per device, on the screen that already owns "how nabu reaches you".
 *
 * It sits inside `Schedule` rather than beside it because a learner looking for "when do
 * you message me" and one looking for "where do you message me" are the same person on
 * the same errand, and the two settings read as contradictory when they are on different
 * screens — quiet hours mean nothing if the notifications arrive anyway, and they do not.
 *
 * **The state this panel shows is per browser, and that is the hard part of it.** A
 * learner with a phone and a laptop has notifications on in one and off in the other, and
 * a panel that reported the account's state would show "on" on the device that is silent.
 * So the switch is driven by `pushManager.getSubscription()` — this browser's own answer —
 * and the server's device count is shown beside it as a separate fact rather than folded
 * into the same sentence.
 */

/** What the panel is currently able to offer. Decided once, on mount. */
type Stage =
  | 'checking'
  /** iOS in a tab: push exists but only for a home-screen install. */
  | 'needs_install'
  | 'unsupported'
  /** The browser has refused, and no button here can change that. */
  | 'denied'
  | 'off'
  | 'on';

export default function Push() {
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>('checking');
  const [devices, setDevices] = useState<number>();
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<CopyKey>();
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState(false);

  /*
   * Read once, and from both sides.
   *
   * The browser says whether *this* device holds a subscription; the API says whether the
   * service has keys at all and how many devices are registered. Neither answers the
   * question alone, and asking only the second is what produces a switch that is on for a
   * device that was never subscribed.
   */
  const refresh = useCallback(async () => {
    const available = support();
    if (available === 'needs_install') {
      setStage('needs_install');
      return;
    }
    if (available === 'unsupported') {
      setStage('unsupported');
      return;
    }

    const state = await getPushState();
    if (!isError(state)) {
      setConfigured(state.configured);
      setDevices(state.devices);
    }

    if (permission() === 'denied') {
      setStage('denied');
      return;
    }
    setStage((await subscribedHere()) ? 'on' : 'off');
  }, []);

  // Deliberately depending on nothing: `t` is a new closure on every language change and
  // listing it would re-run a check that asks the browser for a permission state, once per
  // tap of EN/ES.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * The click handler, and the reason it is not `async` up front.
   *
   * `enablePush` calls `Notification.requestPermission()` before its first `await`,
   * because Safari only honours a permission request still inside the user gesture that
   * triggered it. Anything awaited here before calling it — a state read, a fetch to
   * check something first — ends that gesture and the prompt is refused without ever
   * being shown, which the page then reports as the learner having said no.
   */
  async function turnOn() {
    setBusy(true);
    setProblem(undefined);
    setTested(false);

    const result = await enablePush();
    setBusy(false);

    if (result.ok) {
      setStage('on');
      setDevices(result.devices);
      return;
    }
    if (result.reason === 'denied') {
      setStage(permission() === 'denied' ? 'denied' : 'off');
      return;
    }
    setProblem(result.reason === 'not_configured' ? 'push.error.notConfigured' : 'push.error.generic');
  }

  async function turnOff() {
    setBusy(true);
    setProblem(undefined);
    const ok = await disablePush();
    setBusy(false);
    if (!ok) {
      setProblem('push.error.generic');
      return;
    }
    setStage('off');
    await refresh();
  }

  async function test() {
    setTesting(true);
    setTested(false);
    setProblem(undefined);
    const result = await sendTestMessage();
    setTesting(false);
    if (isError(result)) {
      /*
       * The reason is told apart, because one of these is worth retrying and the others are
       * not. "Try again in a moment" is the right thing to say about a queue that hiccupped
       * and exactly the wrong thing to say to somebody the service has no way to reach —
       * they will press it all evening. Leo's second learner, signed in by email with
       * notifications granted, met the generic copy for a refusal retrying could never fix.
       */
      setProblem(
        result.error === 'unreachable'
          ? 'push.test.unreachable'
          : result.error === 'not_configured'
            ? 'push.error.notConfigured'
            : 'push.test.error',
      );
      return;
    }
    setTested(true);
  }

  /**
   * How many devices the account has, as a sentence rather than a number.
   *
   * `undefined` while the count has not arrived, and nothing is rendered for it — a "0"
   * shown for a fraction of a second beside a switch that says "on" is the same
   * contradiction this panel exists to avoid.
   */
  function deviceLine(): string | undefined {
    if (devices === undefined) return undefined;
    if (devices === 0) return t('push.devices.none');
    if (devices === 1) return t('push.devices.one');
    return t('push.devices.many').replace('{count}', String(devices));
  }

  /*
   * This browser holds a subscription the server has no row for.
   *
   * The pruning rule means the server forgets a device the moment a push service returns
   * 404 or 410, and the browser is not told — so "on here, zero registered" is a real,
   * reachable state in which the learner believes notifications are working and receives
   * none. It is named rather than hidden, and the fix is one round trip of off and on.
   */
  const stale = stage === 'on' && devices === 0;

  return (
    <section className="panel">
      <h2>{t('push.title')}</h2>
      <p className="muted">{t('push.hint')}</p>

      {stage === 'checking' && <p className="muted">{t('push.checking')}</p>}

      {stage === 'needs_install' && (
        <div className="field">
          <strong>{t('push.install.title')}</strong>
          <small>{t('push.install.body')}</small>
        </div>
      )}

      {stage === 'unsupported' && (
        <p className="muted">
          {/* An Apple device in a tab is told how to install; anything else is told the
              browser cannot do it. Same absence of `PushManager`, two different remedies,
              and offering the wrong one sends somebody to a Share menu that will not help. */}
          {isApple() && !installed() ? t('push.install.body') : t('push.unsupported')}
        </p>
      )}

      {stage === 'denied' && (
        <p className="problem" role="alert">
          {t('push.denied')}
        </p>
      )}

      {!configured && stage !== 'checking' && (
        <p className="muted small">{t('push.error.notConfigured')}</p>
      )}

      {(stage === 'off' || stage === 'on') && (
        <div className="form">
          <div className="field">
            <label className="switch">
              <input
                type="checkbox"
                checked={stage === 'on'}
                disabled={busy || !configured}
                onChange={(e) => void (e.target.checked ? turnOn() : turnOff())}
              />
              <span>{stage === 'on' ? t('push.on') : t('push.off')}</span>
            </label>
            {deviceLine() && <small>{deviceLine()}</small>}
          </div>

          {stale && (
            <p className="problem" role="alert">
              {t('push.stale')}
            </p>
          )}

          {/*
            The test, offered whether or not notifications are on here.

            It is not a push test — it goes through the dispatcher and arrives on whatever
            transport this learner is actually on — so it is exactly as useful to somebody
            still on WhatsApp, and hiding it behind the switch would hide the one control
            that answers "is nabu able to reach me at all".
          */}
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void test()}
              disabled={testing}
            >
              {testing ? t('push.test.working') : t('push.test')}
            </button>
            {tested && (
              <span className="muted" role="status">
                {t('push.test.sent')}
              </span>
            )}
          </div>
          <small className="muted">{t('push.test.hint')}</small>

          {busy && <p className="muted small">{t('push.working')}</p>}
        </div>
      )}

      {problem && (
        <p className="problem" role="alert">
          {t(problem)}
        </p>
      )}
    </section>
  );
}
